import { Bundle } from "@medplum/fhirtypes";
import { executeWithNetworkRetries } from "@metriport/shared";
import axios from "axios";
import { z } from "zod";
import {
  OSS_BUNDLE_PAGE_SIZE,
  fetchAllBundlePages,
  type BundlesPageResponse,
} from "./fetch-oss-paginated-bundles";
import { apiOSS } from "./apiOSS";
import { getSandboxSuspects } from "./suspects-mock";
import type { PaginationMetadata } from "../types/pagination-metadata";

const bundleSchema = z.object({
  resourceType: z.literal("Bundle"),
  entry: z
    .array(
      z.object({
        resource: z.record(z.unknown()),
      })
    )
    .optional(),
});

const paginationMetadataSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

const suspectsResponseSchema = z.object({
  metadata: paginationMetadataSchema,
  results: z.array(bundleSchema),
});

type SuspectsResponse = {
  metadata: PaginationMetadata;
  results: Bundle[];
};

async function fetchSuspectsPage(
  patientId: string,
  limit: number,
  offset: number
): Promise<BundlesPageResponse> {
  try {
    const resp = await executeWithNetworkRetries(
      () =>
        apiOSS.get(`/medical/v1/patient/${encodeURIComponent(patientId)}/suspects`, {
          params: { limit, offset },
        }),
      { maxAttempts: 5, initialDelay: 100 }
    );
    const parsed = suspectsResponseSchema.parse(resp.data);
    return parsed as BundlesPageResponse;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return {
        metadata: { total: 0, limit, offset },
        results: [],
      };
    }
    throw error;
  }
}

export async function getSuspects(
  patientId: string,
  isUserInSandbox: boolean
): Promise<SuspectsResponse> {
  if (isUserInSandbox) {
    return getSandboxSuspects(patientId);
  }

  const { results: allBundles } = await fetchAllBundlePages((limit, offset) =>
    fetchSuspectsPage(patientId, limit, offset)
  );

  return {
    metadata: {
      total: allBundles.length,
      limit: OSS_BUNDLE_PAGE_SIZE,
      offset: 0,
    },
    results: allBundles,
  };
}
